import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import FeatherIcon from './FeatherIcon';
import DonateButton from './DonateButton';
import ProductsMenu, { PRODUCT_LINKS } from './ProductsMenu';
import ThemeToggle from '../theme/ThemeToggle';

// Every entry is its own page. Home (the logo) carries the About content.
const linkClass = ({ isActive }: { isActive: boolean }): string =>
  `${isActive ? 'text-primary' : 'text-gray-700'} hover:text-primary transition`;

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = (): void => setIsMenuOpen(false);

  return (
    <nav className="bg-card shadow-md py-4 sticky top-0 z-50">
      {/* `w-full` rather than `container`: the top bar spans the viewport, so the logo and
          the donate CTA sit at the outer edges instead of inside the centred content column. */}
      <div className="w-full px-6 flex justify-between items-center gap-4">
        <Link to="/" className="flex items-center min-w-0 shrink">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center mr-2 sm:mr-3 shrink-0">
            <img
              src="https://res.cloudinary.com/brandpad/image/upload/c_scale,w_auto/v1756758236/33917/logo_4b765b0f"
              alt="Pawsitive Future Foundation"
              className="max-h-full w-auto"
            />
          </div>
          {/* Stacked lockup: side by side, "Pawsitive Future" and "Foundation" collided with
              each other and then with the nav as the viewport narrowed. */}
          <span className="flex flex-col leading-tight min-w-0">
            <span className="text-base sm:text-lg md:text-xl font-bold text-primary truncate">Pawsitive Future</span>
            <span className="text-[11px] sm:text-xs md:text-sm text-gray-600 truncate">Foundation</span>
          </span>
        </Link>

        <div className="hidden xl:flex items-center space-x-6 whitespace-nowrap shrink-0">
          <NavLink to="/voucher-program" className={linkClass}>Voucher Program</NavLink>
          <ProductsMenu />
          <NavLink to="/contact" className={linkClass}>Contact</NavLink>
          <NavLink to="/sign-in" className={linkClass}>Sign in</NavLink>
        </div>

        <ThemeToggle className="hidden xl:inline-flex shrink-0" />

        <DonateButton
          label="Donate Now"
          className="btn-primary px-6 py-2 rounded-full font-medium whitespace-nowrap"
          wrapperClassName="hidden xl:block shrink-0"
        />

        <button
          className="xl:hidden shrink-0 p-2 -mr-2"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
        >
          <FeatherIcon name="menu" />
        </button>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-card shadow-md xl:hidden">
            <div className="flex flex-col p-6 space-y-4">
              <NavLink to="/" end onClick={closeMenu} className={linkClass}>Home</NavLink>
              <NavLink to="/voucher-program" onClick={closeMenu} className={linkClass}>Voucher Program</NavLink>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">Products</p>
                <ul className="mt-2 space-y-2 border-l-2 border-line pl-4">
                  {PRODUCT_LINKS.map(({ to, label }) => (
                    <li key={to}>
                      <NavLink to={to} onClick={closeMenu} className={linkClass}>{label}</NavLink>
                    </li>
                  ))}
                </ul>
              </div>
              <NavLink to="/contact" onClick={closeMenu} className={linkClass}>Contact</NavLink>
              <NavLink to="/sign-in" onClick={closeMenu} className={linkClass}>Sign in</NavLink>
              <ThemeToggle className="self-start" showLabels />
              <DonateButton
                label="Donate Now"
                className="btn-primary px-6 py-2 rounded-full font-medium w-full"
                onNavigate={closeMenu}
              />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;